import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Shield, CheckCircle2, AlertTriangle, XCircle, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';

interface ProtectionCheckResult {
  category: string;
  status: 'pass' | 'warning' | 'fail';
  message: string;
  details?: any;
}

export function SystemAdminProtectionCheck({ accessToken }: { accessToken: string }) {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<ProtectionCheckResult[]>([]);
  const [lastCheck, setLastCheck] = useState<Date | null>(null);

  const runProtectionCheck = async () => {
    setLoading(true);
    setResults([]);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/admin/protection-check`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Protection check failed');
      }

      const data = await response.json();
      setResults(data.results || []);
      setLastCheck(new Date());

      const failCount = data.results.filter((r: ProtectionCheckResult) => r.status === 'fail').length;
      const warningCount = data.results.filter((r: ProtectionCheckResult) => r.status === 'warning').length;

      if (failCount > 0) {
        toast.error(`Protection check failed: ${failCount} critical issue(s) found`);
      } else if (warningCount > 0) {
        toast.warning(`Protection check passed with ${warningCount} warning(s)`);
      } else {
        toast.success('All protection checks passed');
      }
    } catch (error: any) {
      console.error('Protection check error:', error);
      toast.error(error.message || 'Failed to run protection check');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: 'pass' | 'warning' | 'fail') => {
    switch (status) {
      case 'pass':
        return <CheckCircle2 className="h-5 w-5 text-green-600" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
      case 'fail':
        return <XCircle className="h-5 w-5 text-red-600" />;
    }
  };

  const getStatusBadge = (status: 'pass' | 'warning' | 'fail') => {
    switch (status) {
      case 'pass':
        return <Badge className="bg-green-600">Pass</Badge>;
      case 'warning':
        return <Badge className="bg-yellow-600">Warning</Badge>;
      case 'fail':
        return <Badge variant="destructive">Fail</Badge>;
    }
  };

  const overallStatus = results.length > 0 ? (
    results.some(r => r.status === 'fail') ? 'fail' :
    results.some(r => r.status === 'warning') ? 'warning' :
    'pass'
  ) : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          System Admin Data Protection Check
        </CardTitle>
        <CardDescription>
          Verify that System Admin data is protected and not at risk of deletion
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <Shield className="h-4 w-4" />
          <AlertDescription>
            This diagnostic verifies that critical System Admin data (users, global configs, default templates, audit logs) 
            is properly protected and isolated from firm/company deletions.
          </AlertDescription>
        </Alert>

        <div className="flex gap-2">
          <Button 
            onClick={runProtectionCheck} 
            disabled={loading}
            className="flex-1"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Running Checks...
              </>
            ) : (
              <>
                <Shield className="h-4 w-4 mr-2" />
                Run Protection Check
              </>
            )}
          </Button>
          {results.length > 0 && (
            <Button 
              variant="outline"
              onClick={runProtectionCheck}
              disabled={loading}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          )}
        </div>

        {lastCheck && (
          <p className="text-sm text-muted-foreground">
            Last checked: {lastCheck.toLocaleString()}
          </p>
        )}

        {overallStatus && (
          <Alert className={
            overallStatus === 'fail' ? 'border-red-600 bg-red-50 dark:bg-red-950' :
            overallStatus === 'warning' ? 'border-yellow-600 bg-yellow-50 dark:bg-yellow-950' :
            'border-green-600 bg-green-50 dark:bg-green-950'
          }>
            {getStatusIcon(overallStatus)}
            <AlertDescription className="font-semibold">
              {overallStatus === 'fail' && 'CRITICAL: Protection check failed'}
              {overallStatus === 'warning' && 'Warning: Some checks need attention'}
              {overallStatus === 'pass' && 'All protection checks passed'}
            </AlertDescription>
          </Alert>
        )}

        {results.length > 0 && (
          <div className="space-y-3">
            <h3 className="font-semibold">Check Results:</h3>
            <div className="space-y-2">
              {results.map((result, index) => (
                <div 
                  key={index}
                  className={`p-4 border rounded-lg ${
                    result.status === 'fail' ? 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-900' :
                    result.status === 'warning' ? 'bg-yellow-50 dark:bg-yellow-950 border-yellow-200 dark:border-yellow-900' :
                    'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-900'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getStatusIcon(result.status)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="font-medium">{result.category}</span>
                        {getStatusBadge(result.status)}
                      </div>
                      <p className="text-sm text-muted-foreground">{result.message}</p>
                      {result.details && (
                        <div className="mt-2 p-2 bg-white dark:bg-gray-900 rounded text-xs font-mono">
                          <pre>{JSON.stringify(result.details, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {results.length === 0 && !loading && (
          <div className="text-center py-8 text-muted-foreground">
            <Shield className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>Click "Run Protection Check" to verify System Admin data protection</p>
          </div>
        )}

        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-900">
          <h4 className="font-semibold mb-2 flex items-center gap-2">
            <Shield className="h-4 w-4" />
            What This Checks:
          </h4>
          <ul className="text-sm space-y-1 list-disc list-inside text-muted-foreground">
            <li>System Admin users exist and have no firm/company associations</li>
            <li>Global config items are present and not linked to firms/companies</li>
            <li>Default templates exist and are marked correctly</li>
            <li>Audit logs are being preserved</li>
            <li>No System Admin users have firmId or companyId</li>
            <li>Critical system settings are intact</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}